import React, { useState } from 'react';
import { FaHashtag, FaUser, FaPlus, FaSearch } from 'react-icons/fa';
import { useAppContext } from '../../hooks/useAppContext';
import { usePhaserKeyboardSuspend } from '../../hooks/usePhaserKeyboardSuspend';
import PlayerAvatar from '../common/PlayerAvatar';
import type { ChatChannel } from '@shared/schemas/chat.schema.js';

interface ChannelSidebarProps {
  channels: ChatChannel[];
  activeChannel: ChatChannel | null;
  onChannelSelect: (channel: ChatChannel) => void;
  unreadCounts: Record<string, number>;
  onStartDM: (userId: string) => void;
}

export const ChannelSidebar: React.FC<ChannelSidebarProps> = ({
  channels,
  activeChannel,
  onChannelSelect,
  unreadCounts,
  onStartDM
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserList, setShowUserList] = useState(false);
  const { auth, roomConnection } = useAppContext();
  const { playersData } = roomConnection;
  // Search box lives outside Modal/MessageInput, so it must suspend Phaser keys itself
  const phaserKeyboard = usePhaserKeyboardSuspend();

  // Filter channels based on search
  const filteredChannels = channels.filter(channel =>
    (channel.displayName || channel.name).toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Separate public channels and DMs
  const publicChannels = filteredChannels.filter(c => c.type === 'public');
  const dmChannels = filteredChannels.filter(c => c.type === 'dm');

  // Get online users for DM list
  const onlineUsers = playersData.filter(p => p.userId !== auth.user?.id);

  const handleUserClick = (userId: string) => {
    onStartDM(userId);
    setShowUserList(false);
  };

  return (
    <div className="w-[240px] bg-game-navy border-r border-gray-800 flex flex-col">
      {/* Search */}
      <div className="p-3 border-b border-gray-800">
        <div className="relative">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-600 text-sm" />
          <input
            type="text"
            placeholder="Search channels..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={phaserKeyboard.onFocus}
            onBlur={phaserKeyboard.onBlur}
            className="w-full pl-9 pr-3 py-2 bg-game-cream border border-game-medium/50 rounded-md 
                     text-gray-900 placeholder-gray-600 text-sm
                     focus:outline-none focus:border-game-golden focus:bg-game-cream/90 focus:shadow-lg focus:shadow-game-golden/20"
          />
        </div>
      </div>

      {/* Channels List */}
      <div className="flex-1 overflow-y-auto">
        {/* Public Channels */}
        <div className="px-3 py-2">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-300 uppercase">Channels</span>
          </div>
          
          {publicChannels.map(channel => {
            const isActive = activeChannel?.id === channel.id;
            const unreadCount = unreadCounts[channel.id] || 0;
            
            return (
              <button
                key={channel.id}
                onClick={() => onChannelSelect(channel)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left
                          ${isActive 
                            ? 'bg-game-accent/20 text-white' 
                            : 'hover:bg-game-medium/30 text-gray-300 hover:text-white'
                          } ${unreadCount > 0 ? 'font-semibold' : ''}`}
              >
                <FaHashtag className="text-xs flex-shrink-0 text-gray-500" />
                <span className="flex-1 truncate text-sm">
                  {channel.displayName || channel.name}
                </span>
                {unreadCount > 0 && (
                  <span className="bg-game-accent text-white text-xs px-1.5 py-0.5 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* Direct Messages */}
        <div className="px-3 py-2 border-t border-gray-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-300 uppercase">Direct Messages</span>
            <button
              onClick={() => setShowUserList(!showUserList)}
              className="text-gray-400 hover:text-white transition-colors p-1"
              title="Start new DM"
            >
              <FaPlus className="text-xs" />
            </button>
          </div>

          {/* User List for new DMs */}
          {showUserList && (
            <div className="mb-2 p-2 bg-game-medium/20 rounded border border-gray-700">
              <div className="text-xs text-gray-300 mb-2">Select a user:</div>
              <div className="space-y-1 max-h-32 overflow-y-auto">
                {onlineUsers.map(user => (
                  <button
                    key={user.userId}
                    onClick={() => handleUserClick(user.userId)}
                    className="w-full flex items-center gap-2 px-2 py-1 rounded hover:bg-game-medium/30 text-left"
                  >
                    <PlayerAvatar username={user.username} avatar={user.avatar} size="xs" />
                    <span className="text-sm text-gray-300 truncate">{user.username}</span>
                  </button>
                ))}
                {onlineUsers.length === 0 && (
                  <div className="text-xs text-gray-400 italic">No other users online</div>
                )}
              </div>
            </div>
          )}
          
          {dmChannels.map(channel => {
            const isActive = activeChannel?.id === channel.id;
            const unreadCount = unreadCounts[channel.id] || 0;
            
            // Use the dmUser data from the channel itself
            const dmUser = channel.dmUser;
            
            return (
              <button
                key={channel.id}
                onClick={() => onChannelSelect(channel)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded transition-colors text-left
                          ${isActive 
                            ? 'bg-game-accent/20 text-white' 
                            : 'hover:bg-game-medium/30 text-gray-300 hover:text-white'
                          } ${unreadCount > 0 ? 'font-semibold' : ''}`}
              >
                {dmUser ? (
                  <PlayerAvatar username={dmUser.username} avatar={dmUser.avatar} size="xs" />
                ) : (
                  <FaUser className="text-xs flex-shrink-0 text-gray-500" />
                )}
                <span className="flex-1 truncate text-sm">
                  {channel.displayName || 'Unknown User'}
                </span>
                {unreadCount > 0 && (
                  <span className="bg-game-accent text-white text-xs px-1.5 py-0.5 rounded-full">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};